import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../system/admin-access";
import {fetchAllAdminCollection, updateAdminEntity} from "../../system/content-type-admin-api";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {useTranslate} from "../../system/useTranslate";
import {parseDate} from "../../system/utils";

const PRIORITIES = ["normal", "blocked", "urgent"];
const STATUSES = ["new", "in_progress", "resolved", "closed"];
const PRIORITY_SEVERITY = {normal: "info", blocked: "warning", urgent: "danger"};
const STATUS_SEVERITY = {new: "danger", in_progress: "warning", resolved: "success", closed: null};

const formatDate = (value) => value ? parseDate(value, "iso", "YYYY/MM/DD HH:mm", "gregorian", "en") : "-";

function Stat({label, value, severity}) {
    return (
        <div className="col-12 md:col-6 xl:col-3">
            <div className="surface-card border-1 border-200 border-round p-3 flex align-items-center justify-content-between gap-3 h-full">
                <span className="text-sm text-600">{label}</span>
                <Tag value={value} severity={severity}/>
            </div>
        </div>
    );
}

function Field({label, value}) {
    return (
        <div className="flex flex-column gap-1">
            <span className="text-xs text-500">{label}</span>
            <span className="text-sm text-900">{value || "-"}</span>
        </div>
    );
}

function RequestDialog({request, onHide, onSaved}) {
    const translate = useTranslate();
    const [status, setStatus] = useState(request?.requestStatus);
    const [note, setNote] = useState(request?.adminNote || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        setStatus(request?.requestStatus);
        setNote(request?.adminNote || "");
        setError(false);
    }, [request]);

    const save = async () => {
        setSaving(true);
        setError(false);
        try {
            await updateAdminEntity(CONTENT_TYPE_UIDS.SUPPORT_REQUEST, request.documentId, {
                requestStatus: status,
                adminNote: note,
            });
            onSaved({...request, requestStatus: status, adminNote: note});
            onHide();
        } catch (e) {
            setError(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            visible={Boolean(request)}
            onHide={onHide}
            header={request?.subject}
            className="rtl w-11 md:w-8 lg:w-5"
            footer={(
                <div className="flex flex-row-reverse gap-2">
                    <Button className="text-xs" label={translate("save")} loading={saving} onClick={save}/>
                    <Button className="p-button-text text-xs" label={translate("close")} onClick={onHide}/>
                </div>
            )}
        >
            {request && (
                <div className="flex flex-column gap-3">
                    <div className="grid m-0">
                        <div className="col-6"><Field label={translate("support-request-sender")} value={request.fullName}/></div>
                        <div className="col-6"><Field label={translate("phone-number")} value={request.mobile}/></div>
                        <div className="col-6"><Field label={translate("support-request-category")} value={request.categoryTitle}/></div>
                        <div className="col-6"><Field label={translate("support-request-date")} value={formatDate(request.createdAt)}/></div>
                        <div className="col-6">
                            <Tag value={translate(`support-request-priority-${request.priority}`)} severity={PRIORITY_SEVERITY[request.priority]}/>
                        </div>
                    </div>
                    <div className="flex flex-column gap-1">
                        <span className="text-xs text-500">{translate("support-request-message")}</span>
                        <div className="surface-50 border-1 border-200 border-round p-3 text-sm line-height-3" style={{whiteSpace: "pre-wrap"}}>
                            {request.description}
                        </div>
                    </div>
                    <div className="flex flex-column gap-1">
                        <span className="text-xs text-500">{translate("status")}</span>
                        <Dropdown
                            className="w-full text-xs"
                            panelClassName="rtl text-xs"
                            value={status}
                            options={STATUSES.map((value) => ({value, label: translate(`support-request-status-${value}`)}))}
                            onChange={(event) => setStatus(event.value)}
                        />
                    </div>
                    <div className="flex flex-column gap-1">
                        <span className="text-xs text-500">{translate("support-request-admin-note")}</span>
                        <InputTextarea rows={3} className="w-full text-xs" value={note} onChange={(event) => setNote(event.target.value)}/>
                    </div>
                    {error && <small className="p-error">{translate("support-request-save-failed")}</small>}
                </div>
            )}
        </Dialog>
    );
}

function SupportRequests() {
    const translate = useTranslate();
    const adminUser = useSelector((state) => state.auth.user);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [query, setQuery] = useState("");
    const [priority, setPriority] = useState(null);
    const [category, setCategory] = useState(null);
    const [status, setStatus] = useState(null);
    const [selected, setSelected] = useState(null);
    const canView = hasAdminPermission(adminUser, ADMIN_PERMISSIONS.SUPPORT_REQUESTS_VIEW);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const result = await fetchAllAdminCollection(CONTENT_TYPE_UIDS.SUPPORT_REQUEST, {pageSize: 200});
            setRows(result.items);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) load();
    }, [canView, load]);

    const categories = useMemo(
        () => [...new Set(rows.map((row) => row.categoryTitle).filter(Boolean))].map((value) => ({value, label: value})),
        [rows]
    );

    const visible = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        return rows.filter((row) => (
            (!priority || row.priority === priority)
            && (!status || row.requestStatus === status)
            && (!category || row.categoryTitle === category)
            && (!normalized || [row.fullName, row.mobile, row.subject, row.description]
                .some((value) => String(value || "").toLowerCase().includes(normalized)))
        ));
    }, [rows, query, priority, status, category]);

    const count = (predicate) => rows.filter(predicate).length;

    if (!canView) return <AdminAccessDenied/>;

    return (
        <div className="w-full h-full overflow-hidden flex flex-column gap-3 p-3">
            <PageHeaderButton
                title="support-requests"
                description={translate("support-requests-description")}
                end={<Button icon="pi pi-refresh" label={translate("refresh")} className="p-button-sm p-button-outlined gap-2" loading={loading} onClick={load}/>}
            />

            {error && <div className="text-red-500 text-sm px-3">{translate("support-requests-load-failed")}</div>}

            <div className="grid m-0">
                <Stat label={translate("support-requests-total")} value={rows.length} severity="info"/>
                <Stat label={translate("support-request-status-new")} value={count((row) => row.requestStatus === "new")} severity="danger"/>
                <Stat label={translate("support-request-priority-urgent")} value={count((row) => row.priority === "urgent" && !["resolved", "closed"].includes(row.requestStatus))} severity="warning"/>
                <Stat label={translate("support-request-status-resolved")} value={count((row) => row.requestStatus === "resolved")} severity="success"/>
            </div>

            <div className="surface-card border-1 border-200 border-round p-3 flex flex-wrap align-items-center gap-2">
                <span className="p-input-icon-right flex-1 min-w-16rem">
                    <i className="pi pi-search"/>
                    <InputText value={query} onChange={(event) => setQuery(event.target.value)} placeholder={translate("support-requests-search")} className="w-full"/>
                </span>
                <Dropdown value={priority} onChange={(event) => setPriority(event.value)} showClear className="min-w-12rem" placeholder={translate("support-request-priority")}
                          options={PRIORITIES.map((value) => ({value, label: translate(`support-request-priority-${value}`)}))}/>
                <Dropdown value={category} onChange={(event) => setCategory(event.value)} showClear className="min-w-12rem" placeholder={translate("support-request-category")} options={categories}/>
                <Dropdown value={status} onChange={(event) => setStatus(event.value)} showClear className="min-w-12rem" placeholder={translate("status")}
                          options={STATUSES.map((value) => ({value, label: translate(`support-request-status-${value}`)}))}/>
            </div>

            <div className="surface-card border-1 border-200 border-round flex-1 overflow-hidden">
                <DataTable
                    value={visible}
                    dataKey="documentId"
                    loading={loading}
                    scrollable
                    scrollHeight="flex"
                    rowHover
                    emptyMessage={translate("no-records")}
                    onRowClick={(event) => setSelected(event.data)}
                    rowClassName={(row) => `cursor-pointer ${row.requestStatus === "new" ? "font-bold" : ""}`}
                    className="h-full"
                    tableClassName="text-sm"
                >
                    <Column header={translate("support-request-sender")} bodyClassName="text-right" headerClassName="text-right"
                            body={(row) => (
                                <div className="flex flex-column gap-1">
                                    <span className="text-900">{row.fullName}</span>
                                    <span className="text-xs text-500 ltr text-right">{row.mobile}</span>
                                </div>
                            )}/>
                    <Column field="subject" header={translate("support-request-subject")} bodyClassName="text-right" headerClassName="text-right"/>
                    <Column header={translate("support-request-category")} body={(row) => row.categoryTitle || "-"} bodyClassName="text-right" headerClassName="text-right"/>
                    <Column header={translate("support-request-priority")} bodyClassName="text-right" headerClassName="text-right"
                            body={(row) => <Tag value={translate(`support-request-priority-${row.priority}`)} severity={PRIORITY_SEVERITY[row.priority]}/>}/>
                    <Column header={translate("status")} bodyClassName="text-right" headerClassName="text-right"
                            body={(row) => <Tag value={translate(`support-request-status-${row.requestStatus}`)} severity={STATUS_SEVERITY[row.requestStatus]}/>}/>
                    <Column header={translate("support-request-date")} body={(row) => formatDate(row.createdAt)} bodyClassName="text-right ltr" headerClassName="text-right"/>
                </DataTable>
            </div>

            <RequestDialog
                request={selected}
                onHide={() => setSelected(null)}
                onSaved={(saved) => setRows((current) => current.map((row) => row.documentId === saved.documentId ? saved : row))}
            />
        </div>
    );
}

export default SupportRequests;
